import { Injectable } from '@angular/core';
import { environment as env } from '@environments/environment';
import {
  Browser,
  Control,
  FitBoundsOptions,
  FullscreenOptions,
  latLng,
  MapOptions,
  point,
  tileLayer,
  TileLayerOptions
} from 'leaflet';
import { TranslateService } from '@livemaps/i18n';
import { Translations } from '@app/core/services/i18n/translations.service';
import { LocalizationService } from '@app/services/localization.service';
import { Observable, of, switchMap, combineLatest, first, BehaviorSubject } from 'rxjs';
import { FeatureToggleService } from '@app/modules/feature-toggles/services/feature-toggle.service';

export const enum LayerSet {
  LIVE = 'LIVE',
  HISTORY = 'HISTORY'
}

export const enum OverlayCheckboxes {
  CLUSTERING_TOGGLE = 'CLUSTERING_TOGGLE',
  INCIDENTS = 'INCIDENTS',
  ZONES = 'ZONES',
  TRAFFIC = 'TRAFFIC'
}

export const tileLayerBaseOptions: TileLayerOptions = {
  opacity: 0.7,
  subdomains: ['1', '2', '3', '4'],
  detectRetina: true,
  id: '',
  minZoom: 2,
  maxZoom: 20,
  referrerPolicy: 'strict-origin'
};
@Injectable({
  providedIn: 'root'
})
export class LeafletConfigService {
  constructor(
    private translateService: TranslateService,
    private translations: Translations,
    private localization: LocalizationService,
    private featureToggleService: FeatureToggleService
  ) {}

  // ========== Map FitBounds Options ==========
  private mapPadding = {
    default: 100,
    mobileDefault: 10,
    leftDrawerWidth: 368,
    markerLabelWidth: 150
  };

  public defaultBoundingZoom = 16;

  public mobileFitBoundsOptions: FitBoundsOptions = {
    padding: point(this.mapPadding.mobileDefault, this.mapPadding.mobileDefault),
    maxZoom: this.defaultBoundingZoom
  };

  public desktopFitBoundsOptions: FitBoundsOptions = {
    paddingTopLeft: point(this.mapPadding.default + this.mapPadding.leftDrawerWidth, this.mapPadding.default),
    paddingBottomRight: point(this.mapPadding.default + this.mapPadding.markerLabelWidth, this.mapPadding.default),
    maxZoom: this.defaultBoundingZoom
  };
  // ----------------------------------------------

  public defaultFullscreenControlOptions: FullscreenOptions = {
    position: 'bottomright',
    pseudoFullscreen: false,
    title: {
      true: 'Exit Fullscreen',
      false: 'View Fullscreen'
    }
  };

  private defaultLayersControlOptions: Control.LayersOptions = {
    collapsed: true,
    position: 'topright'
  };

  public defaultLocateControlOptions: Control.LocateOptions = {
    position: 'bottomright',
    flyTo: false,
    keepCurrentZoomLevel: true,
    locateOptions: {
      enableHighAccuracy: true
    },
    icon: 'locate-control',
    markerStyle: {
      zIndexOffset: 10000
    },
    clickBehavior: {
      inView: 'stop',
      outOfView: 'setView',
      inViewNotFollowing: 'setView'
    },
    strings: {
      title: 'Show me where I am'
    }
  };

  private defaultZoomControlOptions: Control.ZoomOptions = {
    position: 'bottomright',
    zoomInTitle: 'Zoom in',
    zoomOutTitle: 'Zoom out'
  };

  public isLayersMenuLoaded$ = new BehaviorSubject(false);

  getLocalizedScaleOptions() {
    const useMetric = this.localization.getUseMetricDistance();
    const scaleOptions: Control.ScaleOptions = {
      maxWidth: 200,
      metric: useMetric,
      imperial: !useMetric,
      updateWhenIdle: true,
      position: 'bottomright'
    };
    return scaleOptions;
  }

  /**
   * Returns an Observable containing options object for various leaflet controls
   * with all strings (ie. button text, tooltips) translated
   * @param control string name of the control for which to return options object
   */
  getTranslatedControlOptions$(control: string): Observable<any> {
    switch (control) {
      case 'attribution':
        return this.translateService.get([this.translations.map.attribution.allRightsReserved]).pipe(
          switchMap(translated =>
            of(
              `&copy; 1987 - ${new Date().getFullYear()} <a href="https://legal.here.com/en-gb/terms" target="_blank">HERE</a>.
                &nbsp${translated[this.translations.map.attribution.allRightsReserved]}`
            )
          )
        );
      case 'followAsset':
        return this.translateService
          .get([
            this.translations.map.followAssetControl.followAsset,
            this.translations.map.followAssetControl.followingAsset
          ])
          .pipe(
            switchMap(translated =>
              of({
                followText: translated[this.translations.map.followAssetControl.followAsset],
                followingText: translated[this.translations.map.followAssetControl.followingAsset]
              })
            )
          );
      case 'fullscreen':
        return this.translateService
          .get([
            this.translations.map.fullscreenControl.exitFullscreen,
            this.translations.map.fullscreenControl.viewFullscreen
          ])
          .pipe(
            switchMap(translated =>
              of({
                ...this.defaultFullscreenControlOptions,
                title: {
                  true: translated[this.translations.map.fullscreenControl.exitFullscreen],
                  false: translated[this.translations.map.fullscreenControl.viewFullscreen]
                }
              })
            )
          );
      case 'layers':
        return this.translateService.get([this.translations.map.layersControl.layers]).pipe(
          switchMap(translated =>
            of({
              options: this.defaultLayersControlOptions,
              label: translated[this.translations.map.layersControl.layers]
            })
          )
        );
      case 'locate':
        return this.translateService.get([this.translations.map.locateControl.tooltipText]).pipe(
          switchMap(translated =>
            of({
              ...this.defaultLocateControlOptions,
              strings: {
                title: translated[this.translations.map.locateControl.tooltipText]
              }
            })
          )
        );
      case 'viewEntireTrip':
        return this.translateService
          .get([this.translations.map.viewEntireTripControl.viewEntireTrip])
          .pipe(switchMap(translated => of(translated[this.translations.map.viewEntireTripControl.viewEntireTrip])));
      case 'zoom':
        return this.translateService
          .get([this.translations.map.zoomControl.zoomIn, this.translations.map.zoomControl.zoomOut])
          .pipe(
            switchMap(translated =>
              of({
                ...this.defaultZoomControlOptions,
                zoomInTitle: translated[this.translations.map.zoomControl.zoomIn],
                zoomOutTitle: translated[this.translations.map.zoomControl.zoomOut]
              })
            )
          );
      default:
        return of({});
    }
  }

  private hereTileUrl = (scheme: string, resource: string = 'base'): string => {
    const size = 256;
    const ppi = Browser.retina && scheme !== 'explore.satellite.day' ? 400 : 100; // only normal (100 ppi) can be used with satellite.day
    const poiState = 'all';
    const featuresPoiParam = scheme != 'satellite.day' ? `&features=pois:${poiState}` : '';
    const lg = this.localization.getMapTileLanguage();

    return `https://maps.hereapi.com/v3/${resource}/mc/{z}/{x}/{y}/png8?size=${size}&style=${scheme}&ppi=${ppi}${featuresPoiParam}&lang=${lg}&apiKey=${env.here.apiKeyV3}`;
  };

  public tileLayers = {
    NORMAL_DAY: tileLayer(this.hereTileUrl('explore.day'), { ...tileLayerBaseOptions }),
    HYBRID_DAY: tileLayer(this.hereTileUrl('explore.satellite.day'), { ...tileLayerBaseOptions })
  };

  public defaultMapOptions: MapOptions = {
    attributionControl: false,
    layers: [this.tileLayers.NORMAL_DAY],
    zoom: 5,
    zoomControl: false,
    zoomSnap: 0.5,
    center: latLng(47.602004, -122.335295),
    // there is a known issue with leaflet version 1.7.1 and mobile safari that causes two click click events to emit.
    // to avoid this issue, we disable tap events entirely, as described in the comments of this issue:
    // https://github.com/Leaflet/Leaflet/issues/7255
    tap: false
  };

  public translatedBaseLayers: Object;

  getTranslatedBaseLayers$(): Observable<any> {
    return this.translateService
      .get([this.translations.map.layersControl.classicMap, this.translations.map.layersControl.satellite])
      .pipe(
        switchMap(translated => {
          this.translatedBaseLayers = {};
          // HISTORY mode has only 3 options
          this.translatedBaseLayers[translated[this.translations.map.layersControl.classicMap]] = 'NORMAL_DAY';
          this.translatedBaseLayers[translated[this.translations.map.layersControl.satellite]] = 'HYBRID_DAY';

          // return object must map translated strings to tile URLs
          const returnLayers = {};
          Object.keys(this.translatedBaseLayers).forEach(key => {
            returnLayers[key] = this.tileLayers[this.translatedBaseLayers[key]];
          });
          return of(returnLayers);
        })
      );
  }

  public translatedOverlays = {};

  getTranslatedOverlays$(clusterTogglePsuedoLayer, incidentGroup, zoneGroup, trafficGroup, layers): Observable<any> {
    return combineLatest([
      this.translateService.get([
        this.translations.map.checkboxOptions.clusterAssets,
        this.translations.map.checkboxOptions.roadIncidents,
        this.translations.map.checkboxOptions.showZones,
        this.translations.map.checkboxOptions.traffic
      ]),
      this.featureToggleService.isFeatureEnabled('zones-enabled').pipe(first())
    ]).pipe(
      switchMap(([translated, areZonesEnabled]) => {
        this.translatedOverlays[translated[this.translations.map.checkboxOptions.clusterAssets]] =
          OverlayCheckboxes.CLUSTERING_TOGGLE;
        this.translatedOverlays[translated[this.translations.map.checkboxOptions.roadIncidents]] =
          OverlayCheckboxes.INCIDENTS;

        this.translatedOverlays[translated[this.translations.map.checkboxOptions.showZones]] = OverlayCheckboxes.ZONES;

        this.translatedOverlays[translated[this.translations.map.checkboxOptions.traffic]] = OverlayCheckboxes.TRAFFIC;

        const returnLayers = {
          [translated[this.translations.map.checkboxOptions.clusterAssets]]: clusterTogglePsuedoLayer,
          [translated[this.translations.map.checkboxOptions.roadIncidents]]: incidentGroup,
          [translated[this.translations.map.checkboxOptions.traffic]]: trafficGroup,
          [translated[this.translations.map.checkboxOptions.showZones]]: zoneGroup
        };

        if (layers === LayerSet.HISTORY) {
          delete returnLayers[translated[this.translations.map.checkboxOptions.clusterAssets]];
        }
        // devcycle feature flag for areZonesEnabled determines if "Show Zones" buttton is shown on layers menu
        if (!areZonesEnabled) {
          delete returnLayers[translated[this.translations.map.checkboxOptions.showZones]];
        }
        // return object maps translated strings to the leaflet objects passed as args
        return of(returnLayers);
      })
    );
  }

  addLayerCssClasses(): void {
    const classes = ['classic', 'satellite'];
    const layersLabels = document
      ?.getElementsByClassName('leaflet-control-layers-base')[0]
      ?.getElementsByTagName('label');
    for (let i = 0; i < layersLabels?.length; i++) {
      const span = layersLabels[i]?.getElementsByTagName('span')[0].getElementsByTagName('span')[0];
      span?.classList?.add(classes[i]);
    }

    const overlayClasses = [
      'cypress-cluster-toggle',
      'cypress-incident-toggle',
      'cypress-traffic-toggle',
      'cypress-show-zones-toggle'
    ];
    const overlayLabels = document
      ?.getElementsByClassName('leaflet-control-layers-overlays')[0]
      ?.getElementsByTagName('label');
    for (let i = 0; i < overlayLabels?.length; i++) {
      const span = overlayLabels[i]?.getElementsByTagName('span')[0]?.getElementsByTagName('span')[0];
      span?.classList?.add(overlayClasses[i]);

      const input = overlayLabels[i]?.getElementsByTagName('span')[0]?.getElementsByTagName('input')[0];
      input?.classList?.add(overlayClasses[i]);

      if (overlayClasses[i] === 'cypress-show-zones-toggle') {
        input.id = 'pendo_gtcx_zone_checkbox';
      }
    }
  }
}
